import React, { useLayoutEffect, useEffect, useMemo, useState } from "react";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { useDispatch, useSelector } from "react-redux";
import ReactECharts from "echarts-for-react";
import { GET_DATAGUARD_HEATMAP_DATA } from "../Utility/DataguardConstant";
import { startCase, camelCase } from "lodash";
import { maskUsername } from "../../SysGuard/SysGuardCommonComp";

const formatCount = (value) => {
  if (value == null) return "0";

  const format = (num, suffix) => {
    if (num >= 100) {
      return `${Math.floor(num)}${suffix}`;
    }

    if (num >= 10) {
      return `${num.toFixed(1).replace(/\.0$/, "")}${suffix}`;
    }

    return `${num.toFixed(1).replace(/\.0$/, "")}${suffix}`;
  };

  if (value >= 1_000_000_000) {
    return format(value / 1_000_000_000, "B");
  }

  if (value >= 1_000_000) {
    return format(value / 1_000_000, "M");
  }

  if (value >= 1_000) {
    return format(value / 1_000, "K");
  }

  return value.toString();
};

const DataguardHeatmap = ({ loc }) => {
  let cellTotalWidth, limitResults, maxChartCols;
  const containerRef = React.useRef(null);
  const [maxCols, setMaxCols] = useState(2);
  const dispatch = useDispatch();
  const heatmapDataDataGuard = useSelector(
    (state) => state.dataTagReducer?.userDashboardData,
  );
  const heatMapDataGeneral = useSelector(
    (state) => state.DataguardReducer?.heatmapData,
  );

  const heatmapData = Array.isArray(heatmapDataDataGuard)
    ? heatmapDataDataGuard
    : Array.isArray(heatMapDataGeneral)
      ? heatMapDataGeneral
      : [];
  if (loc == "dashboardPopup") {
    cellTotalWidth = 85;
    maxChartCols = 9;
    limitResults = 100;
  } else {
    cellTotalWidth = 55;
    limitResults = 50;
    maxChartCols = 7;
    if (heatmapData && heatmapData.length) {
      heatmapData.splice(50);
    }
  }
  if (heatmapData && heatmapData.length) {
    heatmapData.sort((a, b) => a.username.localeCompare(b.username));
  }
  const heatmapLoader = useSelector(
    (state) => state.DataguardReducer.heatmapLoader,
  );
  const isIncognito = useSelector((state) => state.DataguardReducer.incognito);
  const updateMaxCols = () => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    let calculatedCols = Math.max(
      1,
      Math.floor(containerWidth / cellTotalWidth) - 1,
    );
    calculatedCols = Math.min(calculatedCols, maxChartCols); // Cap at 7 columns
    setMaxCols(calculatedCols);
  };
  // Initial calculation when DOM is ready
  useLayoutEffect(() => {
    updateMaxCols();
  });

  // Setup ResizeObserver and window resize to handle container resizing and zoom
  useEffect(() => {
    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateMaxCols, 50);
    };

    const resizeObserver = new ResizeObserver(debouncedUpdate);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize for zoom events
    window.addEventListener("resize", debouncedUpdate);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedUpdate);
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatch({
      type: GET_DATAGUARD_HEATMAP_DATA,
      payload: { limit: limitResults },
    });
  }, [dispatch]);

  const { usernames, maxCount } = useMemo(() => {
    if (!heatmapData) return { usernames: [], maxCount: 100 };

    const uniqueUsernames = [
      ...new Set(heatmapData?.map((item) => item.username)),
    ];

    const maxCount = Math.max(
      ...heatmapData?.map((item) => item.totalActivities),
    );

    return { usernames: uniqueUsernames, maxCount };
  }, [heatmapData]);

  const getInitials = (username) => {
    const initials = username
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
    // Limit initials to maximum 3 characters to prevent overflow
    return initials.length > 3 ? initials.substring(0, 3) : initials;
  };

  const formatCellText = (initials, activities, cellWidth) => {
    const activityText = formatCount(activities);

    const text = `${initials} (${activityText})`;

    // If it fits, show in one line
    if (text.length <= 10 || cellWidth > 60) {
      return text;
    }

    // Otherwise split into two lines
    return `${initials}\n(${activityText})`;
  };

  const getOption = () => {
    if (!heatmapData) return {};

    // Calculate grid dimensions based on number of items
    const totalItems = heatmapData.length;
    const cols = Math.max(1, Math.min(maxCols, totalItems));
    const rows = Math.ceil(totalItems / cols);

    // Transform data for grid layout
    const data = heatmapData?.map((item, index) => {
      const lastRowItemCount = totalItems % cols || cols;
      const isLastRow = Math.floor(index / cols) === rows - 1;
      const offsetX = isLastRow ? Math.floor((cols - lastRowItemCount) / 2) : 0;

      return {
        value: [
          (index % cols) + (isLastRow ? offsetX : 0),
          rows - 1 - Math.floor(index / cols),
          item.totalActivities,
          getInitials(item.username),
          item.uniqueFiles,
        ],
      };
    });

    return {
      tooltip: {
        position: "top",
        formatter: function (params) {
          const user = heatmapData[params.dataIndex];

          const displayName = isIncognito
            ? maskUsername(user.username)
            : startCase(camelCase(user.username));

          return `
    <div style="min-width:180px">
      <div
  style="
    font-size:15px;
    font-weight:600;
    margin-bottom:12px;
    color:#1F2937;
    border-bottom:1px solid #E5E7EB;
    padding-bottom:8px;
  "
>
  ${displayName}
</div>

      <div style="margin-bottom:8px;">
        <span style="color:#6B7280">Total Activities</span><br/>
        <strong>${user.totalActivities.toLocaleString()}</strong>
      </div>

      <div>
        <span style="color:#6B7280">Unique Files</span><br/>
        <strong>${user.uniqueFiles.toLocaleString()}</strong>
      </div>
    </div>
  `;
        },
      },
      grid: {
        top: "5%",
        bottom: "5%",
        left: "5%",
        right: "5%",
        height: "auto",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        splitArea: { show: true },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        data: Array(cols)
          .fill(0)
          .map((_, i) => i),
      },
      yAxis: {
        type: "category",
        splitArea: { show: true },
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        data: Array(rows)
          .fill(0)
          .map((_, i) => i),
      },
      visualMap: {
        show: false,
        dimension: 2, // Explicitly tell it to use value[2] (count) for coloring
        min: 0, // Start from 0 to handle zero values
        max: maxCount,
        inRange: {
          color: ["#f0f2ffff", "#8e99d7ff", "#4355B9"], // Very light blue for 0, light blue for low values, dark blue for high values
        },
        calculable: true,
        realtime: true,
        // Use piecewise to better distribute colors across the value range
        piecewise: true,
        scale: true,
        pieces: [
          { value: 0, color: "#ffffffff" }, // Very light blue for 0 values (disabled appearance)
          { min: 1, max: Math.ceil(maxCount * 0.3), color: "#8e99d7ff" }, // Light blue for low values
          {
            min: Math.ceil(maxCount * 0.3) + 1,
            max: Math.ceil(maxCount * 0.7),
            color: "#8e99d7ff",
          }, // Medium blue for mid values
          { min: Math.ceil(maxCount * 0.7) + 1, color: "#4355B9" }, // Dark blue for high values
        ],
      },
      series: [
        {
          name: "User Activity",
          type: "heatmap",
          data: data,
          cellSize: [cellTotalWidth, cellTotalWidth], // Fixed width and height for each cell
          aspectRatio: undefined, // Remove aspect ratio to allow rectangular cells
          progressive: 100,
          animation: false,
          label: {
            show: true,
            position: "inside",
            formatter: function (params) {
              const initials = params.value[3];
              const count = params.value[2];
              return formatCellText(initials, count, cellTotalWidth);
            },
            rich: {
              initial: {
                fontSize: cellTotalWidth <= 55 ? 12 : 16,
                fontWeight: "bold",
                color: "#FFFFFF",
                padding: [0, 0, 2, 0],
                lineHeight: cellTotalWidth <= 55 ? 12 : 16,
              },
              count: {
                fontSize: cellTotalWidth <= 55 ? 10 : 14,
                color: "#FFFFFF",
                padding: [2, 0, 0, 0],
                lineHeight: cellTotalWidth <= 55 ? 10 : 14,
              },
            },
            // Add text overflow handling
            overflow: "truncate",
            textStyle: {
              fontSize: cellTotalWidth <= 55 ? 10 : 12,
            },
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: "#fff",
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  if (heatmapLoader) {
    return (
      <div className="loader-container">
        <img src={Page_Loader} alt="loading" className="loader-img" />
      </div>
    );
  }
  if (!heatmapData || heatmapData.length === 0) return "No data available";
  const cols = Math.max(1, Math.min(maxCols, heatmapData.length));
  const rows = Math.ceil(heatmapData.length / cols);
  const chartWidth = cols * cellTotalWidth;
  const chartHeight = rows * cellTotalWidth;
  return (
    <div ref={containerRef} className="heatmap-container">
      <ReactECharts
        className="heatmap-chart"
        option={getOption()}
        style={{
          height: `${chartHeight}px`,
          width: loc === "dashboardPopup" ? `${chartWidth}px` : "290px" ,
        }}
        notMerge={true}
        opts={{
          renderer: "canvas",
        }}
      />
    </div>
  );
};

export default DataguardHeatmap;